import { useNavigate } from '@tanstack/react-router'
import { Command } from 'cmdk'
import { CalendarDays, FileText, Layers, MessageSquarePlus, Settings2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { usePanel } from '@/app/panel'
import { searchMeetings } from '@/data/meetings'
import { templatesSync } from '@/data/templates'

/**
 * The palette searches the same store the pages read, through the same ranker
 * the chat's search step uses — so "I found it in the palette but not in
 * Meetings" cannot happen. Meetings open in the side panel rather than
 * navigating, because the palette is usually opened from somewhere the reader
 * wants to stay.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const panel = usePanel()
  const [query, setQuery] = useState('')

  const meetings = useMemo(
    () => (query.trim().length > 2 ? searchMeetings(query, 6) : []),
    [query]
  )

  function go(action: () => void) {
    onOpenChange(false)
    setQuery('')
    action()
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-[oklch(0.15_0.01_265_/_0.4)] p-4 pt-[12vh] backdrop-blur-[2px]"
      onClick={() => onOpenChange(false)}
    >
      <Command
        label="Command palette"
        // cmdk's own fuzzy filter runs on the static entries; once a query is
        // long enough to search meetings, the ranker owns the ordering and
        // cmdk re-sorting the results on top of it would undo the scoring.
        shouldFilter={meetings.length === 0}
        className="w-full max-w-xl overflow-hidden rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <Command.Input
          value={query}
          onValueChange={setQuery}
          autoFocus
          placeholder="Search meetings, spaces and templates…"
          className="w-full border-b border-[var(--color-border)] bg-transparent px-4 py-3 text-sm text-[var(--color-fg)] outline-none placeholder:text-[var(--color-fg-subtle)]"
        />
        <Command.List className="max-h-96 overflow-y-auto p-1.5">
          <Command.Empty className="px-3 py-8 text-center text-xs text-[var(--color-fg-subtle)]">
            Nothing matches.
          </Command.Empty>

          {meetings.length > 0 ? (
            <Command.Group heading="Meetings">
              {meetings.map((meeting) => (
                <Item
                  key={meeting.id}
                  icon={CalendarDays}
                  onSelect={() => go(() => panel.open({ type: 'meeting', meetingId: meeting.id }))}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{meeting.title}</span>
                    <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
                      {new Date(meeting.startsAt).toLocaleDateString()} ·{' '}
                      {meeting.noteCount} note{meeting.noteCount === 1 ? '' : 's'}
                    </span>
                  </span>
                </Item>
              ))}
            </Command.Group>
          ) : null}

          <Command.Group heading="Go to">
            <Item icon={MessageSquarePlus} onSelect={() => go(() => navigate({ to: '/chat' }))}>
              New chat
            </Item>
            <Item icon={CalendarDays} onSelect={() => go(() => navigate({ to: '/meetings' }))}>
              Meetings
            </Item>
            <Item icon={FileText} onSelect={() => go(() => navigate({ to: '/templates' }))}>
              Templates
            </Item>
            <Item icon={Layers} onSelect={() => go(() => navigate({ to: '/spaces' }))}>
              Spaces
            </Item>
            {/* Not in the sidebar, so the palette is the only way to reach it
                by name rather than by remembering it is under the avatar. */}
            <Item icon={Settings2} onSelect={() => go(() => navigate({ to: '/settings' }))}>
              Settings
            </Item>
          </Command.Group>

          <Command.Group heading="Templates">
            {templatesSync().map((template) => (
              <Item
                key={template.id}
                icon={FileText}
                onSelect={() => go(() => navigate({ to: '/templates' }))}
              >
                {template.name}
              </Item>
            ))}
          </Command.Group>
        </Command.List>
      </Command>
    </div>
  )
}

function Item({
  icon: Icon,
  onSelect,
  children,
}: {
  icon: typeof FileText
  onSelect: () => void
  children: React.ReactNode
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex cursor-default items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-2 text-sm text-[var(--color-fg)] data-[selected=true]:bg-[var(--color-surface-hover)]"
    >
      <Icon className="size-3.5 shrink-0 text-[var(--color-fg-subtle)]" />
      {children}
    </Command.Item>
  )
}

/** ⌘K / Ctrl-K. Registered once at the shell, not per route. */
export function useCommandPalette() {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setOpen((value) => !value)
      }
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
  return { open, setOpen }
}
